import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getRestaurantMenus } from "../api/menu";
import { changeOrderStatus } from "../api/orders";
import {
  changeReservationStatus,
  getFloorPlan,
  getReservationSettings,
  getRestaurantReservations,
  getTableBlocks,
  getTables,
} from "../api/reservations";
import { getSubscription } from "../api/subscriptions";
import { FoodServiceContext } from "../context/FoodServiceContext";
import { UserContext } from "../context/UserContext";
import { buildDashboardViewModel } from "../services/dashboardViewModel";
import { errorMessage } from "../utils/errorMessage";

const initialResources = {
  menus: null,
  reservations: null,
  tables: null,
  tableBlocks: null,
  floorPlan: null,
  reservationSettings: null,
  subscription: null,
};

export const useRestaurantDashboard = () => {
  const { user } = useContext(UserContext);
  const {
    restaurantData,
    orders,
    loading: restaurantLoading,
    error: restaurantError,
    ordersLoading,
    ordersError,
    setOrders,
    fetchOrders,
  } = useContext(FoodServiceContext);
  const restaurantId = user?._id;
  const requestVersion = useRef(0);
  const [resources, setResources] = useState(initialResources);
  const [resourceErrors, setResourceErrors] = useState({});
  const [loadingResources, setLoadingResources] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [actionState, setActionState] = useState({ busyKey: null, error: null, scope: null });

  const load = useCallback(async () => {
    if (!restaurantId) return;

    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setLoadingResources(true);
    setResourceErrors({});

    const requests = {
      menus: getRestaurantMenus(restaurantId),
      reservations: getRestaurantReservations(restaurantId),
      tables: getTables(restaurantId),
      tableBlocks: getTableBlocks(restaurantId),
      floorPlan: getFloorPlan(restaurantId),
      reservationSettings: getReservationSettings(restaurantId),
      subscription: getSubscription(restaurantId),
    };
    const keys = Object.keys(requests);
    const results = await Promise.allSettled(Object.values(requests));
    if (requestVersion.current !== version) return;

    const nextResources = {};
    const nextErrors = {};
    results.forEach((result, index) => {
      const key = keys[index];
      if (result.status === "fulfilled") {
        nextResources[key] = result.value;
      } else {
        nextResources[key] = null;
        nextErrors[key] = errorMessage(result.reason, "دریافت اطلاعات داشبورد ناموفق بود.");
      }
    });

    setResources(nextResources);
    setResourceErrors(nextErrors);
    setLoadingResources(false);
    setHasLoaded(true);
  }, [restaurantId]);

  useEffect(() => {
    setResources(initialResources);
    setResourceErrors({});
    setHasLoaded(false);
    load();
    return () => {
      requestVersion.current += 1;
    };
  }, [load]);

  const data = useMemo(
    () =>
      buildDashboardViewModel({
        restaurant: restaurantData || user,
        restaurantAvailable: !restaurantError,
        menus: resources.menus,
        reservations: resources.reservations,
        tables: resources.tables,
        tableBlocks: resources.tableBlocks,
        floorPlan: resources.floorPlan,
        reservationSettings: resources.reservationSettings,
        orders,
        ordersAvailable: !ordersError,
        subscription: resources.subscription,
      }),
    [orders, ordersError, resources, restaurantData, restaurantError, user]
  );

  const errors = useMemo(
    () => ({
      ...resourceErrors,
      ...(restaurantError ? { restaurant: restaurantError } : {}),
      ...(ordersError ? { orders: ordersError } : {}),
    }),
    [ordersError, resourceErrors, restaurantError]
  );

  const isInitialLoading = !hasLoaded || restaurantLoading;
  const failedResourceCount = Object.keys(errors).length;
  const status = isInitialLoading
    ? "loading"
    : failedResourceCount === 0
      ? "success"
      : failedResourceCount >= 9
        ? "error"
        : "partial";

  const updateReservationStatus = useCallback(
    async (reservationId, nextStatus) => {
      const busyKey = `reservation:${reservationId}:${nextStatus}`;
      setActionState({ busyKey, error: null, scope: "reservation" });
      try {
        const updatedReservation = await changeReservationStatus(restaurantId, reservationId, nextStatus);
        setResources((current) => ({
          ...current,
          reservations: Array.isArray(current.reservations)
            ? current.reservations.map((reservation) =>
                reservation._id === updatedReservation._id
                  ? {
                      ...reservation,
                      status: updatedReservation.status,
                      cancellationReason: updatedReservation.cancellationReason,
                    }
                  : reservation
              )
            : current.reservations,
        }));
        setActionState({ busyKey: null, error: null, scope: null });
        return true;
      } catch (error) {
        setActionState({
          busyKey: null,
          error: errorMessage(error, "تغییر وضعیت رزرو ناموفق بود."),
          scope: "reservation",
        });
        return false;
      }
    },
    [restaurantId]
  );

  const updateOrderStatus = useCallback(
    async (orderId, nextStatus) => {
      const busyKey = `order:${orderId}:${nextStatus}`;
      setActionState({ busyKey, error: null, scope: "order" });
      try {
        const updatedOrder = await changeOrderStatus(orderId, nextStatus);
        if (updatedOrder) {
          setOrders((currentOrders) =>
            currentOrders.map((order) => (order._id === updatedOrder._id ? updatedOrder : order))
          );
        }
        setActionState({ busyKey: null, error: null, scope: null });
        return true;
      } catch (error) {
        setActionState({
          busyKey: null,
          error: errorMessage(error, "تغییر وضعیت سفارش ناموفق بود."),
          scope: "order",
        });
        return false;
      }
    },
    [setOrders]
  );

  const refresh = useCallback(
    async () => Promise.all([load(), fetchOrders()]),
    [fetchOrders, load]
  );

  return {
    data,
    status,
    loading: isInitialLoading,
    refreshing: hasLoaded && (loadingResources || ordersLoading),
    resourceLoading: {
      reservations: !hasLoaded,
      orders: ordersLoading,
      floorPlan: !hasLoaded,
    },
    errors,
    empty:
      status === "success" &&
      !data.menu.exists &&
      data.today.reservationCount === 0 &&
      data.recent.orders.length === 0,
    refresh,
    actions: {
      updateReservationStatus,
      updateOrderStatus,
      clearError: () => setActionState((current) => ({ ...current, error: null, scope: null })),
    },
    actionState,
  };
};

export default useRestaurantDashboard;
